import React, { useState, useMemo } from "react";

export default function ConfigListPanel({
  configs,
  selectedConfigId,
  onSelectConfig,
  onNewConfig,
  onDeleteConfig,
  meta,
}) {
  const [showFilters, setShowFilters] = useState(false);
  const [filterGroupId, setFilterGroupId] = useState(null);
  const [filterCategoryId, setFilterCategoryId] = useState(null);

  // Helper function to get config label
  const getConfigLabel = (config) => {
    const groupName = config.group_name || meta.groups.find(g => String(g.id) === String(config.group_id))?.name || "";
    const categoryName = config.category_name || meta.categories.find(c => String(c.id) === String(config.category_id))?.name || "";

    if (config.group_id == null && config.category_id == null) {
      return "글로벌 기본값";
    } else if (config.group_id == null && config.category_id != null) {
      return `${categoryName} (전체 그룹)`;
    } else if (config.group_id != null && config.category_id == null) {
      return `${groupName} (전체 카테고리)`;
    } else {
      return `${groupName} / ${categoryName}`;
    }
  };

  // Separate global config and filtered configs
  const { globalConfig, filteredConfigs } = useMemo(() => {
    const global = configs.find(c => c.group_id == null && c.category_id == null);
    
    let filtered = configs.filter(c => {
      // Exclude global from regular list
      if (c.group_id == null && c.category_id == null) return false;
      
      // Apply filters
      if (filterGroupId && String(c.group_id) !== String(filterGroupId)) return false;
      if (filterCategoryId && String(c.category_id) !== String(filterCategoryId)) return false;
      
      return true;
    });

    return { globalConfig: global, filteredConfigs: filtered };
  }, [configs, filterGroupId, filterCategoryId]);

  const handleCardClick = (configId) => {
    onSelectConfig(configId);
  };

  const handleDeleteClick = (e, configId) => {
    e.stopPropagation();
    if (window.confirm("이 설정을 삭제하시겠습니까?")) {
      onDeleteConfig(configId);
    }
  };

  const renderConfigCard = (config, isGlobal = false) => {
    const isSelected = selectedConfigId === config.config_id;
    const isActive = config.is_active;
    const label = getConfigLabel(config);
    
    return (
      <div
        key={config.config_id}
        onClick={() => handleCardClick(config.config_id)}
        className={`
          relative p-4 rounded-lg cursor-pointer transition-all duration-200 group
          ${isGlobal 
            ? 'border-2 border-indigo-500/50 bg-slate-800/50' 
            : 'border border-slate-700 bg-slate-800/30'
          }
          ${isSelected 
            ? 'border-indigo-500 bg-indigo-500/30 shadow-lg shadow-indigo-500/20' 
            : 'hover:border-slate-600 hover:bg-slate-800/50'
          }
        `}
      >
        {/* Delete Button */}
        {!isGlobal && (
          <button
            onClick={(e) => handleDeleteClick(e, config.config_id)}
            className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-red-400 hover:text-red-300 p-1 rounded hover:bg-red-500/20"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        {/* Active Indicator + Label */}
        <div className="flex items-center gap-2 mb-2">
          <div className={`
            w-2 h-2 rounded-full shrink-0
            ${isActive 
              ? 'bg-emerald-400 shadow-lg shadow-emerald-400/50 animate-pulse' 
              : 'bg-slate-500'
            }
          `} />
          <div className="font-black text-sm text-slate-100 truncate">
            {isGlobal && "🌐 "}
            {label}
          </div>
        </div>

        {/* Description */}
        {config.description && (
          <div className="text-xs text-slate-400 line-clamp-2 mb-2">
            {config.description}
          </div>
        )}

        {/* Updated Date */}
        <div className="text-xs text-slate-500">
          {new Date(config.updated_at || config.created_at).toLocaleDateString('ko-KR')}
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col bg-slate-900">
      {/* Header */}
      <div className="p-4 border-b border-slate-700">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-black text-lg text-slate-100">설정 목록</h2>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`
              p-2 rounded-lg transition-colors
              ${showFilters 
                ? 'text-indigo-400 bg-indigo-500/20' 
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
              }
            `}
            title="필터 토글"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
          </button>
        </div>
        
        <button
          onClick={onNewConfig}
          className="w-full py-2 px-4 bg-indigo-600 hover:bg-indigo-500 text-white font-black text-sm rounded-lg transition-colors shadow-lg shadow-indigo-500/30"
        >
          + 새 설정
        </button>
      </div>

      {/* Filter Section */}
      {showFilters && (
        <div className="p-4 border-b border-slate-700 space-y-3 bg-slate-800/30">
          {/* Group Filter */}
          <div>
            <label className="block text-xs font-black text-slate-300 mb-1">그룹</label>
            <select
              value={filterGroupId || ""}
              onChange={(e) => setFilterGroupId(e.target.value || null)}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-100 focus:outline-none focus:border-indigo-500 transition-colors"
            >
              <option value="">전체 그룹</option>
              {meta.groups.map(group => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>

          {/* Category Filter */}
          <div>
            <label className="block text-xs font-black text-slate-300 mb-1">카테고리</label>
            <select
              value={filterCategoryId || ""}
              onChange={(e) => setFilterCategoryId(e.target.value || null)}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-100 focus:outline-none focus:border-indigo-500 transition-colors"
            >
              <option value="">전체 카테고리</option>
              {meta.categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Config List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar">
        {/* Global Config - Always Pinned */}
        {globalConfig && renderConfigCard(globalConfig, true)}

        {/* Filtered Configs */}
        {filteredConfigs.length > 0 ? (
          filteredConfigs.map(config => renderConfigCard(config))
        ) : (
          <div className="text-center py-12">
            <div className="text-slate-500 text-sm">
              {configs.length === 0 || (configs.length === 1 && globalConfig)
                ? "설정이 없습니다"
                : "검색 결과가 없습니다"
              }
            </div>
          </div>
        )}
      </div>

      {/* Custom Scrollbar Styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(15, 23, 42, 0.5);
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(71, 85, 105, 0.5);
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(71, 85, 105, 0.8);
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}
